<template>
    <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" max-width="700"
        :fullscreen="isMobile" persistent scrollable @keydown.esc="chiudi">
        <v-card>
            <v-card-title>
                {{ solaLettura ? 'Dettaglio pagamento' : (form.id ? 'Modifica pagamento' : 'Nuovo pagamento') }}
                <v-chip v-if="solaLettura && form.stato" class="ml-2" size="small" :color="coloreRiga(form)"
                    variant="flat">
                    {{ etichettaRiga(form) }}
                </v-chip>
            </v-card-title>
            <v-card-text>
                <v-form ref="formPagamento" :disabled="solaLettura">
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-select v-model="form.tipoPagamentoId" :items="tipiPagamento" item-title="nome"
                                item-value="id" label="Tipologia" :rules="[v => !!v || 'Campo obbligatorio']" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model.number="form.importo" type="number" step="0.01" label="Importo (€)"
                                :rules="[v => v !== null && v !== '' || 'Campo obbligatorio']" />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-select v-model="form.fornitoreId" :items="fornitori" item-title="nome" item-value="id"
                                label="Fornitore (opzionale)" clearable />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.beneficiarioNote" label="Beneficiario libero (se senza fornitore)" />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.dataScadenza" type="date" label="Data scadenza"
                                :rules="[v => !!v || 'Campo obbligatorio']" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.dataPagamento" type="date" label="Data pagamento"
                                :disabled="solaLettura || form.stato !== 'Pagato'" />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.idDocumentoInterno" label="ID documento interno (opzionale)"
                                :readonly="documentiReadonly && !form.id" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.numDocumentoInterno" label="Numero documento interno (opzionale)"
                                :readonly="documentiReadonly && !form.id" />
                        </v-col>
                    </v-row>

                    <v-textarea v-model="form.descrizione" label="Descrizione" rows="2" />
                    <v-textarea v-model="form.notePagamento" label="Note" rows="2" />

                    <v-checkbox v-model="form.ricorrente"
                        label="Rendi ricorrente (pianifica subito le prossime occorrenze, a data fissa)"
                        hint="Se non selezionato, alla chiusura verrà comunque generato automaticamente un solo prossimo pagamento, calcolato dalla data di pagamento effettiva."
                        persistent-hint />
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn variant="text" @click="chiudi">{{ solaLettura ? 'Chiudi' : 'Annulla' }}</v-btn>
                <v-btn v-if="!solaLettura" color="primary" :loading="salvataggio" @click="salva">Salva</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { coloreRiga, etichettaRiga } from "@/constants/statoPagamento.js";
import storage from "@/services/storage.js";

export default {
    name: "DialogPagamento",

    props: {
        modelValue: { type: Boolean, default: false },
        pagamento: { type: Object, default: null },
        // Usato anche dal calendario unificato per mostrare il dettaglio di un
        // evento Pagamento in sola visualizzazione (vedi Calendario.vue), non
        // essendo stato creato un dialog di dettaglio dedicato separato.
        solaLettura: { type: Boolean, default: false },
        // Precompilazione per un NUOVO pagamento (es. da un link con parametri in
        // query, vedi Pagamenti.vue): ignorata quando si modifica un pagamento
        // esistente (il prop "pagamento" ha sempre la precedenza).
        valoriIniziali: { type: Object, default: null },
        // Se true, blocca la modifica di idDocumentoInterno/numDocumentoInterno
        // quando precompilati dall'URL (solo in creazione, mai in modifica: vedi
        // "readonly" in Pagamenti.vue).
        documentiReadonly: { type: Boolean, default: false },
    },
    emits: ["update:modelValue", "salvato"],

    data() {
        return {
            form: this.formVuoto(),
            salvataggio: false,
        };
    },

    computed: {
        isMobile() {
            return this.$vuetify.display.smAndDown;
        },
        tipiPagamento() {
            return this.$store.getters["tipiPagamento/lista"];
        },
        fornitori() {
            return this.$store.getters["fornitori/lista"];
        },
    },

    watch: {
        modelValue(val) {
            if (val) this.inizializza();
        },

        // Bozza persistita solo per un NUOVO pagamento (mai in modifica di uno
        // esistente): sopravvive alla navigazione verso Tipi pagamento/Fornitori,
        // raggiunti dai bottoni dentro Pagamenti.vue per creare al volo
        // un'anagrafica mancante senza perdere quanto già digitato qui. Viene
        // ripristinata da Pagamenti.vue al rimontaggio (vedi ripristinaBozzaSePresente).
        form: {
            deep: true,
            handler(val) {
                if (this.modelValue && !this.solaLettura && !val.id) {
                    storage.setBozza("pagamento", val);
                }
            },
        },
    },

    methods: {
        coloreRiga,
        etichettaRiga,

        formVuoto() {
            return {
                id: null,
                tipoPagamentoId: null,
                fornitoreId: null,
                beneficiarioNote: "",
                importo: null,
                stato: "DaPagare",
                dataScadenza: new Date().toISOString().substr(0, 10),
                dataPagamento: null,
                descrizione: "",
                notePagamento: "",
                ricorrente: false,
                idDocumentoInterno: "",
                numDocumentoInterno: "",
            };
        },

        async inizializza() {
            this.form = this.pagamento
                ? {
                    id: this.pagamento.id,
                    tipoPagamentoId: this.pagamento.tipoPagamentoId,
                    fornitoreId: this.pagamento.fornitoreId,
                    beneficiarioNote: this.pagamento.beneficiarioNote,
                    importo: this.pagamento.importo,
                    stato: this.pagamento.stato,
                    dataScadenza: this.toDateInput(this.pagamento.dataScadenza),
                    dataPagamento: this.toDateInput(this.pagamento.dataPagamento),
                    descrizione: this.pagamento.descrizione,
                    notePagamento: this.pagamento.notePagamento,
                    ricorrente: this.pagamento.ricorrente ?? false,
                    idDocumentoInterno: this.pagamento.idDocumentoInterno || "",
                    numDocumentoInterno: this.pagamento.numDocumentoInterno || "",
                }
                : { ...this.formVuoto(), ...this.valoriIniziali };

            await Promise.all([
                this.$store.dispatch("tipiPagamento/carica"),
                this.$store.dispatch("fornitori/carica"),
            ]);
        },

        toDateInput(data) {
            return data ? new Date(data).toISOString().substr(0, 10) : null;
        },

        async salva() {
            if (!this.$refs.formPagamento.validate()) return;

            this.salvataggio = true;
            try {
                const { id, ...dati } = this.form;
                delete dati.stato;
                await this.$store.dispatch("pagamenti/salva", { id, dati });
                storage.clearBozza("pagamento");
                this.$emit("salvato");
                this.chiudi();
            } catch (error) {
                console.log(error);
            }
            this.salvataggio = false;
        },

        chiudi() {
            if (!this.form.id) storage.clearBozza("pagamento");
            this.$emit("update:modelValue", false);
        },
    },
};
</script>
